/* SponsorsMediaPanel — TS/AD Sponsors tab (doc: /app/docs/OL_SPONSORS.md).
 *
 * Two sub-tabs:
 *   • Sponsors  — sponsor records as CARDS (default) or a compact TABLE with
 *                 inline edits (name, sport, tier, remaining term, website,
 *                 active). Logos are added / replaced / removed right on the
 *                 card, the table row or the Add/Edit form: OL resizes to the
 *                 org's logo size, saves WebP and uploads to WP uploads.
 *   • Tier Mgmt — sponsor tiers per sport (name, sport, price, carousel weight).
 *
 * Website visibility (AJ 2026-10-12): a sponsor shows while Active, has a logo
 * and its term has not ended. The term starts when the logo is added and runs
 * the org's term length (default 12 months); Renew adds another term and the
 * Remaining (months) cell can be typed directly.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import {
  Building2, Link2, Loader2, Plus, Save, Trash2, X, Award, LayoutGrid, Table2, RefreshCw, ImagePlus,
} from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { useTheme } from '../contexts/ThemeContext';

const API = process.env.REACT_APP_BACKEND_URL;
const NEW_TIER = '__new__';
const VIEW_KEY = 'ol.sponsors.view';
const DAYS_PER_MONTH = 30.44;

const readView = () => { try { return localStorage.getItem(VIEW_KEY) || 'cards'; } catch { return 'cards'; } };
const writeView = (v) => { try { localStorage.setItem(VIEW_KEY, v); } catch { /* ignore */ } };
const errMsg = (err, fb) => err?.response?.data?.detail || fb;
/** Names imported from WP can carry HTML entities (e.g. "Al&#039;s"). */
const decode = (str) => {
  if (!str || !str.includes('&')) return str || '';
  const t = document.createElement('textarea');
  t.innerHTML = str;
  return t.value;
};

/** Remaining term in months (1 decimal, never negative). */
const remainingMonths = (s) => (s.remaining_days == null ? null
  : Math.max(0, Math.round((s.remaining_days / DAYS_PER_MONTH) * 10) / 10));

const fmtDate = (iso) => {
  if (!iso) return '—';
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
};

function status(s) {
  if (!s.active) return { label: 'Inactive', cls: 'border-slate-400 text-slate-500' };
  if (!s.logo_url) return { label: 'No logo', cls: 'border-amber-500 text-amber-600' };
  if (s.remaining_days != null && s.remaining_days < 0) return { label: 'Expired', cls: 'border-red-400 text-red-500' };
  return { label: 'On website', cls: 'border-emerald-500 text-emerald-600' };
}

export default function SponsorsMediaPanel({ orgId }) {
  const { isDark } = useTheme();
  const [tab, setTab] = useState('sponsors');
  const [view, setView] = useState(readView);
  const [sponsors, setSponsors] = useState([]);
  const [tiers, setTiers] = useState([]);
  const [sports, setSports] = useState([]);
  const [settings, setSettings] = useState({ term_months: 12, logo_max_px: 500 });
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    if (!orgId) return;
    setLoading(true);
    try {
      const [sp, ti, so] = await Promise.all([
        axios.get(`${API}/api/sponsors/${orgId}`),
        axios.get(`${API}/api/sponsors/${orgId}/tiers`),
        axios.get(`${API}/api/organizations/${orgId}/sports`),
      ]);
      setSponsors(sp.data?.data || []);
      if (sp.data?.settings) setSettings(sp.data.settings);
      setTiers(ti.data?.data || []);
      // De-dupe sports by name (the sports collection can carry stub dupes).
      const seen = new Set();
      const names = (so.data || [])
        .map((s) => (s.name || s.label || '').trim())
        .filter((n) => n && !seen.has(n.toLowerCase()) && seen.add(n.toLowerCase()));
      setSports(names);
    } catch (err) {
      console.error('Failed to load sponsors:', err);
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  useEffect(() => { load(); }, [load]);

  const putRow = (row) => setSponsors((list) => list.map((s) => (s.id === row.id ? row : s)));
  const switchView = (v) => { setView(v); writeView(v); };

  const tierName = (slug) => tiers.find((t) => t.slug === slug)?.name || slug || '—';
  const sportName = (key) => (!key || key === 'all') ? 'All sports'
    : (sports.find((n) => n.toLowerCase() === key) || key);
  /** Tiers configured for a sport (Tier Mgmt) + org-wide tiers; "All sports" sees every tier. */
  const tiersFor = (sport) => {
    const sp = (sport || 'all').toLowerCase();
    return tiers.filter((t) => sp === 'all' || !t.sport || t.sport === sp);
  };
  const tierOptions = (sport, current) => {
    const list = tiersFor(sport);
    const opts = list.map((t) => ({ v: t.slug, l: (sport || 'all') === 'all' && t.sport ? `${t.name} (${sportName(t.sport)})` : t.name }));
    if (current && !list.some((t) => t.slug === current)) {
      opts.unshift({ v: current, l: `⚠ ${tierName(current)} — not a ${sportName(sport)} tier`, bad: true });
    }
    return opts;
  };

  // ── API actions ────────────────────────────────────────────────────────────
  const patch = async (s, body) => {
    setBusyId(s.id);
    try {
      const { data } = await axios.patch(`${API}/api/sponsors/${orgId}/${s.id}`, body);
      putRow(data.data);
      return data.data;
    } catch (err) { alert(errMsg(err, 'Save failed')); return null; }
    finally { setBusyId(null); }
  };
  const renew = async (s) => {
    setBusyId(s.id);
    try { const { data } = await axios.post(`${API}/api/sponsors/${orgId}/${s.id}/renew`); putRow(data.data); return data.data; }
    catch (err) { alert(errMsg(err, 'Renew failed')); return null; }
    finally { setBusyId(null); }
  };
  const uploadLogo = async (id, file) => {
    const fd = new FormData();
    fd.append('file', file);
    setBusyId(id);
    try {
      const { data } = await axios.post(`${API}/api/sponsors/${orgId}/${id}/logo`, fd,
        { headers: { 'Content-Type': 'multipart/form-data' } });
      putRow(data.data);
      return data.data;
    } catch (err) { alert(errMsg(err, 'Logo upload failed')); return null; }
    finally { setBusyId(null); }
  };
  const removeLogo = async (s) => {
    if (!window.confirm(`Remove the logo for ${s.name}? It will drop off the website sponsor bar.`)) return null;
    setBusyId(s.id);
    try { const { data } = await axios.delete(`${API}/api/sponsors/${orgId}/${s.id}/logo`); putRow(data.data); return data.data; }
    catch (err) { alert(errMsg(err, 'Remove failed')); return null; }
    finally { setBusyId(null); }
  };
  const deleteSponsor = async (s) => {
    if (!window.confirm(`Delete sponsor ${s.name}? This also removes it from the website.`)) return;
    try { await axios.delete(`${API}/api/sponsors/${orgId}/${s.id}`); setSponsors((l) => l.filter((x) => x.id !== s.id)); }
    catch (err) { alert(errMsg(err, 'Delete failed')); }
  };
  const saveSetting = async (key, val) => {
    const n = parseInt(val, 10);
    if (!n || n === settings[key]) return;
    try { const { data } = await axios.put(`${API}/api/sponsors/${orgId}/settings`, { [key]: n }); setSettings(data.data); await load(); }
    catch (err) { alert(errMsg(err, 'Could not save setting')); }
  };
  const createTier = async (sport) => {
    const name = window.prompt(`Name the new ${sportName(sport)} sponsor tier (e.g. "Champion")`);
    if (!name || !name.trim()) return null;
    try {
      const { data } = await axios.post(`${API}/api/sponsors/${orgId}/tiers`,
        { name: name.trim(), sport: sport && sport !== 'all' ? sport : '' });
      await load();
      return data.data.slug;
    } catch (err) { alert(errMsg(err, 'Could not create tier')); return null; }
  };

  // ── Add / Edit form ────────────────────────────────────────────────────────
  const startNew = () => setDraft({ name: '', tier: '', sport: 'all', website_url: '', active: true, logo_url: '', _file: null, _preview: '' });
  const startEdit = (s) => setDraft({ ...s, name: decode(s.name), sport: s.sport || 'all', _file: null, _preview: '', _remaining: remainingMonths(s) });

  const saveDraft = async () => {
    if (!draft?.name) return;
    setSaving(true);
    try {
      const body = { name: draft.name, tier: draft.tier || '', sport: draft.sport || 'all',
        website_url: draft.website_url || '', active: !!draft.active };
      let row;
      if (draft.id) {
        if (draft._remaining !== '' && draft._remaining != null && Number(draft._remaining) !== remainingMonths(draft)) {
          body.remaining_months = Number(draft._remaining);
        }
        row = (await axios.patch(`${API}/api/sponsors/${orgId}/${draft.id}`, body)).data.data;
      } else {
        row = (await axios.post(`${API}/api/sponsors/${orgId}`, body)).data.data;
        setSponsors((l) => [...l, row]);
      }
      if (draft._removeLogo && row.logo_url) {
        await axios.delete(`${API}/api/sponsors/${orgId}/${row.id}/logo`);
      }
      if (draft._file) await uploadLogo(row.id, draft._file);
      await load();
      setDraft(null);
    } catch (err) { alert(errMsg(err, 'Save failed')); }
    finally { setSaving(false); }
  };

  if (!orgId) return null;

  const cardCls = isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200';
  const textCls = isDark ? 'text-white' : 'text-slate-900';
  const mutedCls = isDark ? 'text-slate-400' : 'text-slate-500';
  const inputCls = isDark ? 'bg-slate-700/50 border-slate-600 text-white' : '';
  const selCls = `w-full rounded-md px-3 py-2 text-sm border ${isDark ? 'bg-slate-700/50 border-slate-600 text-white' : 'bg-white border-slate-300'}`;
  const cellIn = `w-full bg-transparent rounded px-1 py-0.5 text-xs border border-transparent hover:border-slate-300 focus:border-amber-500 focus:outline-none ${isDark ? 'text-white hover:border-slate-600' : 'text-slate-900'}`;
  const ui = { isDark, textCls, mutedCls, cellIn };

  return (
    <div className="space-y-4" data-testid="ad-sponsors-media-panel">
      <div className="flex items-center gap-2">
        <Button size="sm" variant={tab === 'sponsors' ? 'default' : 'outline'}
          className={tab === 'sponsors' ? 'bg-amber-600 hover:bg-amber-700 text-white' : ''}
          onClick={() => setTab('sponsors')} data-testid="sponsors-subtab-sponsors">Sponsors</Button>
        <Button size="sm" variant={tab === 'tiers' ? 'default' : 'outline'}
          className={tab === 'tiers' ? 'bg-amber-600 hover:bg-amber-700 text-white' : ''}
          onClick={() => setTab('tiers')} data-testid="sponsors-subtab-tiers">
          <Award className="h-4 w-4 mr-1" />Tier Mgmt</Button>
      </div>

      {tab === 'tiers' ? (
        <TierManager orgId={orgId} tiers={tiers} sports={sports} reload={load}
          isDark={isDark} cardCls={cardCls} textCls={textCls} mutedCls={mutedCls} inputCls={inputCls}
          usageCount={(slug) => sponsors.filter((s) => (s.tier || '') === slug).length} />
      ) : (
      <Card className={cardCls}>
        <CardContent className="p-3 space-y-3">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className="flex items-center gap-2">
              <span className={`text-sm font-semibold ${textCls}`}>Sponsor records</span>
              <span className={`text-xs ${mutedCls}`}>
                {loading ? '—' : `${sponsors.length} configured · ${sponsors.filter((s) => s.visible).length} on website`}
              </span>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <label className={`text-[11px] ${mutedCls}`} title="Length of one sponsorship term; Renew adds one more term">
                Term
                <input type="number" min="1" max="60" defaultValue={settings.term_months} key={`t${settings.term_months}`}
                  onBlur={(e) => saveSetting('term_months', e.target.value)}
                  className={`mx-1 w-12 rounded border px-1 py-0.5 text-xs ${isDark ? 'bg-slate-700 border-slate-600 text-white' : 'border-slate-300'}`}
                  data-testid="sponsor-setting-term" />months
              </label>
              <label className={`text-[11px] ${mutedCls}`} title="Logos are resized so the longest side is this many pixels, then saved as WebP">
                Logo size
                <input type="number" min="100" max="2000" step="50" defaultValue={settings.logo_max_px} key={`l${settings.logo_max_px}`}
                  onBlur={(e) => saveSetting('logo_max_px', e.target.value)}
                  className={`mx-1 w-14 rounded border px-1 py-0.5 text-xs ${isDark ? 'bg-slate-700 border-slate-600 text-white' : 'border-slate-300'}`}
                  data-testid="sponsor-setting-logo-px" />px
              </label>
              <div className={`flex rounded-md border overflow-hidden ${isDark ? 'border-slate-600' : 'border-slate-300'}`}>
                {[['cards', LayoutGrid, 'Cards'], ['table', Table2, 'Table']].map(([v, Icon, l]) => (
                  <button key={v} onClick={() => switchView(v)} data-testid={`sponsor-view-${v}`}
                    className={`px-2 py-1 text-xs flex items-center gap-1 ${view === v ? 'bg-amber-600 text-white' : mutedCls}`}>
                    <Icon className="h-3.5 w-3.5" />{l}
                  </button>
                ))}
              </div>
              <Button size="sm" onClick={startNew} disabled={!!draft}
                className="bg-amber-600 hover:bg-amber-700 text-white h-8" data-testid="sponsor-add-btn">
                <Plus className="h-4 w-4 mr-1" />Add sponsor
              </Button>
            </div>
          </div>

          {draft && (
            <DraftForm draft={draft} setDraft={setDraft} saving={saving} onSave={saveDraft} onCancel={() => setDraft(null)}
              sports={sports} tierOptions={tierOptions} createTier={createTier} settings={settings}
              isDark={isDark} mutedCls={mutedCls} textCls={textCls} inputCls={inputCls} selCls={selCls} />
          )}

          {loading ? (
            <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-amber-500" /></div>
          ) : sponsors.length === 0 ? (
            <p className={`text-xs italic text-center py-8 ${mutedCls}`}>No sponsor records yet. Click <strong>+ Add sponsor</strong> above to create one.</p>
          ) : view === 'table' ? (
            <SponsorTable sponsors={sponsors} sports={sports} busyId={busyId} ui={ui}
              tierOptions={tierOptions} createTier={createTier}
              patch={patch} renew={renew} uploadLogo={uploadLogo} removeLogo={removeLogo} del={deleteSponsor} />
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2" data-testid="sponsor-gallery">
              {sponsors.map((s) => (
                <SponsorCard key={s.id} s={s} busy={busyId === s.id} ui={ui} disabled={!!draft}
                  tierName={tierName} sportName={sportName}
                  onEdit={() => startEdit(s)} onDelete={() => deleteSponsor(s)} onRenew={() => renew(s)}
                  onUpload={(f) => uploadLogo(s.id, f)} onRemoveLogo={() => removeLogo(s)} />
              ))}
            </div>
          )}
        </CardContent>
      </Card>
      )}
    </div>
  );
}

/** Logo tile with add / replace / remove. `onFile` receives the chosen File. */
function LogoTile({ src, name, size = 'h-14 w-14', busy, onFile, onRemove, isDark, testid }) {
  const ref = useRef(null);
  return (
    <div className={`relative group flex-shrink-0 ${size}`} data-testid={testid}>
      {src ? (
        <img src={src} alt={name} className={`${size} object-contain bg-white rounded border border-slate-200`}
          onError={(e) => { e.target.style.visibility = 'hidden'; }} />
      ) : (
        <div className={`${size} rounded flex items-center justify-center border border-dashed ${isDark ? 'bg-slate-700 border-slate-500' : 'bg-slate-50 border-slate-300'}`}>
          <Building2 className="h-6 w-6 text-slate-400" />
        </div>
      )}
      {busy ? (
        <div className="absolute inset-0 bg-black/40 rounded flex items-center justify-center"><Loader2 className="h-4 w-4 animate-spin text-white" /></div>
      ) : (
        <div className="absolute inset-0 rounded bg-black/55 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-1">
          <button type="button" title={src ? 'Replace logo' : 'Add logo'} onClick={() => ref.current?.click()}
            className="p-1 rounded bg-white/90 text-slate-800" data-testid={`${testid}-upload`}><ImagePlus className="h-3.5 w-3.5" /></button>
          {src && onRemove && (
            <button type="button" title="Remove logo" onClick={onRemove}
              className="p-1 rounded bg-white/90 text-red-600" data-testid={`${testid}-remove`}><Trash2 className="h-3.5 w-3.5" /></button>
          )}
        </div>
      )}
      <input ref={ref} type="file" accept="image/*" className="hidden"
        onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) onFile(f); }} />
    </div>
  );
}

function TermLine({ s, mutedCls }) {
  if (!s.term_end) return <span className={`text-[11px] ${mutedCls}`}>Term starts when a logo is added</span>;
  const m = remainingMonths(s);
  return (
    <span className={`text-[11px] ${s.remaining_days < 0 ? 'text-red-500' : mutedCls}`}
      title={s.term_derived ? 'Estimated from the logo upload month — Renew or edit Remaining to set it' : `Term ${fmtDate(s.term_start)} – ${fmtDate(s.term_end)}`}>
      {s.remaining_days < 0 ? `Ended ${fmtDate(s.term_end)}` : `${m} mo left · ends ${fmtDate(s.term_end)}`}{s.term_derived ? ' (est.)' : ''}
    </span>
  );
}

function SponsorCard({ s, busy, ui, disabled, tierName, sportName, onEdit, onDelete, onRenew, onUpload, onRemoveLogo }) {
  const { isDark, textCls, mutedCls } = ui;
  const st = status(s);
  return (
    <div className={`relative rounded border p-3 flex items-center gap-3 ${isDark ? 'bg-slate-900/40 border-slate-700' : 'bg-white border-slate-200'} ${s.visible ? '' : 'opacity-75'}`}
      data-testid={`sponsor-card-${s.id}`}>
      <LogoTile src={s.logo_url} name={s.name} busy={busy} onFile={onUpload} onRemove={onRemoveLogo} isDark={isDark} testid={`sponsor-logo-${s.id}`} />
      <div className="flex-1 min-w-0">
        <div className={`font-semibold text-sm truncate ${textCls}`} title={decode(s.name)}>{decode(s.name)}</div>
        <div className="flex items-center gap-1 mt-0.5 flex-wrap">
          <Badge variant="outline" className={`text-[10px] ${isDark ? 'border-amber-500/40 text-amber-300' : 'border-amber-400 text-amber-700'}`}>{tierName(s.tier)}</Badge>
          <Badge variant="outline" className={`text-[10px] ${isDark ? 'border-teal-500/40 text-teal-300' : 'border-teal-400 text-teal-700'}`}>{sportName(s.sport)}</Badge>
          <Badge variant="outline" className={`text-[10px] ${st.cls}`}>{st.label}</Badge>
        </div>
        <div className="mt-1 flex items-center gap-2 flex-wrap">
          <TermLine s={s} mutedCls={mutedCls} />
          {s.website_url && (
            <a href={s.website_url} target="_blank" rel="noreferrer"
              className={`inline-flex items-center gap-1 text-[11px] ${isDark ? 'text-amber-400' : 'text-amber-600'}`}>
              <Link2 className="h-3 w-3" />website
            </a>
          )}
        </div>
      </div>
      <div className="flex flex-col gap-1">
        <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={onEdit} disabled={disabled} data-testid={`sponsor-edit-${s.id}`}>Edit</Button>
        <Button size="sm" variant="ghost" className="h-7 px-2 text-xs text-emerald-600" onClick={onRenew} disabled={busy}
          title="Add one more term" data-testid={`sponsor-renew-${s.id}`}><RefreshCw className="h-3 w-3 mr-1" />Renew</Button>
        <Button size="sm" variant="ghost" className="h-7 px-2 text-xs text-red-400 hover:text-red-300" onClick={onDelete} data-testid={`sponsor-delete-${s.id}`}>
          <Trash2 className="h-3 w-3" />
        </Button>
      </div>
    </div>
  );
}

function TierSelect({ value, sport, tierOptions, createTier, onChange, className, testid }) {
  return (
    <select value={value || ''} className={className} data-testid={testid}
      onChange={async (e) => {
        if (e.target.value !== NEW_TIER) { onChange(e.target.value); return; }
        const slug = await createTier(sport);
        if (slug) onChange(slug);
      }}>
      <option value="">— none —</option>
      {tierOptions(sport, value).map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
      <option value={NEW_TIER}>+ New tier…</option>
    </select>
  );
}

function SportSelect({ value, sports, onChange, className, testid }) {
  return (
    <select value={value || 'all'} onChange={(e) => onChange(e.target.value)} className={className} data-testid={testid}>
      <option value="all">All sports</option>
      {sports.map((n) => <option key={n} value={n.toLowerCase()}>{n}</option>)}
    </select>
  );
}

/** Compact table — every cell saves on change/blur. */
function SponsorTable({ sponsors, sports, busyId, ui, tierOptions, createTier, patch, renew, uploadLogo, removeLogo, del }) {
  const { isDark, mutedCls, cellIn } = ui;
  const th = `px-2 py-1.5 text-left text-[10px] font-semibold uppercase tracking-wide ${mutedCls}`;
  const sel = `${cellIn} cursor-pointer`;
  const textCell = (s, key, ph) => (
    <input defaultValue={decode(s[key])} key={`${s.id}-${key}-${s[key]}`} placeholder={ph} className={cellIn}
      onBlur={(e) => { const v = e.target.value.trim(); if (v !== decode(s[key]) && (key !== 'name' || v)) patch(s, { [key]: v }); }}
      onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }} data-testid={`sponsor-cell-${key}-${s.id}`} />
  );
  return (
    <div className={`overflow-x-auto rounded border ${isDark ? 'border-slate-700' : 'border-slate-200'}`}>
      <table className="w-full text-xs" data-testid="sponsor-table">
        <thead className={isDark ? 'bg-slate-900/60' : 'bg-slate-50'}>
          <tr>
            <th className={th}>Logo</th><th className={th}>Sponsor</th><th className={th}>Sport</th><th className={th}>Tier</th>
            <th className={th} title="Months left in the term — type a value or press Renew">Remaining (mo)</th>
            <th className={th}>Ends</th><th className={th}>Website</th><th className={th}>Active</th><th className={th}>Status</th><th className={th} />
          </tr>
        </thead>
        <tbody>
          {sponsors.map((s) => {
            const st = status(s);
            const busy = busyId === s.id;
            return (
              <tr key={s.id} className={`border-t ${isDark ? 'border-slate-700/60' : 'border-slate-100'}`} data-testid={`sponsor-row-${s.id}`}>
                <td className="px-2 py-1">
                  <LogoTile src={s.logo_url} name={s.name} size="h-8 w-12" busy={busy} isDark={isDark}
                    onFile={(f) => uploadLogo(s.id, f)} onRemove={() => removeLogo(s)} testid={`sponsor-row-logo-${s.id}`} />
                </td>
                <td className="px-1 py-1 min-w-[160px]">{textCell(s, 'name', 'Company name')}</td>
                <td className="px-1 py-1 min-w-[110px]">
                  <SportSelect value={s.sport} sports={sports} className={sel} testid={`sponsor-cell-sport-${s.id}`}
                    onChange={(v) => patch(s, { sport: v })} />
                </td>
                <td className="px-1 py-1 min-w-[130px]">
                  <TierSelect value={s.tier} sport={s.sport} tierOptions={tierOptions} createTier={createTier} className={sel}
                    testid={`sponsor-cell-tier-${s.id}`} onChange={(v) => patch(s, { tier: v })} />
                </td>
                <td className="px-1 py-1 whitespace-nowrap">
                  <div className="flex items-center gap-1">
                    <input type="number" step="0.5" min="0" style={{ width: 58 }}
                      className={`${cellIn.replace('w-full ', '')} shrink-0 text-right border-slate-200`}
                      defaultValue={remainingMonths(s) ?? ''} key={`${s.id}-rm-${s.term_end}`} placeholder="—"
                      title={s.term_derived ? 'Estimated from the logo upload month' : ''}
                      onBlur={(e) => {
                        const v = e.target.value;
                        if (v !== '' && Number(v) !== remainingMonths(s)) patch(s, { remaining_months: Number(v) });
                      }}
                      onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }} data-testid={`sponsor-cell-remaining-${s.id}`} />
                    <button type="button" disabled={busy} onClick={() => renew(s)} title="Add one more term"
                      className="px-1.5 py-0.5 rounded text-[10px] font-semibold text-emerald-700 border border-emerald-400 hover:bg-emerald-50 disabled:opacity-50"
                      data-testid={`sponsor-cell-renew-${s.id}`}>Renew</button>
                  </div>
                </td>
                <td className={`px-2 py-1 whitespace-nowrap ${s.remaining_days < 0 ? 'text-red-500' : mutedCls}`}>
                  {fmtDate(s.term_end)}{s.term_derived && s.term_end ? '*' : ''}
                </td>
                <td className="px-1 py-1 min-w-[150px]">{textCell(s, 'website_url', 'https://')}</td>
                <td className="px-2 py-1 text-center">
                  <input type="checkbox" checked={!!s.active} onChange={(e) => patch(s, { active: e.target.checked })}
                    data-testid={`sponsor-cell-active-${s.id}`} />
                </td>
                <td className="px-2 py-1"><Badge variant="outline" className={`text-[10px] whitespace-nowrap ${st.cls}`}>{st.label}</Badge></td>
                <td className="px-1 py-1">
                  <button type="button" onClick={() => del(s)} className="p-1 text-red-400 hover:text-red-600" title="Delete sponsor"
                    data-testid={`sponsor-cell-delete-${s.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {sponsors.some((s) => s.term_derived && s.term_end) && (
        <p className={`px-2 py-1 text-[10px] ${mutedCls}`}>* Estimated from the month the logo was uploaded — Renew or type Remaining to set it.</p>
      )}
    </div>
  );
}

function DraftForm({ draft, setDraft, saving, onSave, onCancel, sports, tierOptions, createTier, settings,
  isDark, mutedCls, textCls, inputCls, selCls }) {
  const set = (k, v) => setDraft((p) => ({ ...p, [k]: v }));
  const lbl = `text-[11px] font-semibold uppercase tracking-wide ${mutedCls}`;
  const shownLogo = draft._preview || (draft._removeLogo ? '' : draft.logo_url);
  return (
    <div className={`p-3 rounded border ${isDark ? 'bg-slate-900/40 border-slate-700' : 'bg-slate-50 border-slate-200'} space-y-2`} data-testid="sponsor-edit-form">
      <div className="flex gap-3 items-start">
        <div className="space-y-1">
          <span className={lbl}>Logo</span>
          <LogoTile src={shownLogo} name={draft.name} size="h-20 w-20" isDark={isDark} testid="sponsor-edit-logo"
            onFile={(f) => setDraft((p) => ({ ...p, _file: f, _preview: URL.createObjectURL(f), _removeLogo: false }))}
            onRemove={() => setDraft((p) => ({ ...p, _file: null, _preview: '', _removeLogo: true }))} />
          <p className={`text-[10px] w-24 ${mutedCls}`}>Saved as WebP, {settings.logo_max_px}px</p>
        </div>
        <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-2">
          <div>
            <label className={lbl}>Title (company name)</label>
            <Input value={draft.name} onChange={(e) => set('name', e.target.value)}
              placeholder="e.g., Sabres Pediatrics" className={inputCls} data-testid="sponsor-edit-name" />
          </div>
          <div>
            <label className={lbl}>Sport</label>
            <SportSelect value={draft.sport} sports={sports} className={selCls} testid="sponsor-edit-sport"
              onChange={(v) => set('sport', v)} />
          </div>
          <div>
            <label className={lbl}>Tier</label>
            <TierSelect value={draft.tier} sport={draft.sport} tierOptions={tierOptions} createTier={createTier}
              className={selCls} testid="sponsor-edit-tier" onChange={(v) => set('tier', v)} />
          </div>
          <div>
            <label className={lbl}>Remaining term (months)</label>
            {draft.id ? (
              <Input type="number" step="0.5" min="0" value={draft._remaining ?? ''} placeholder="—"
                onChange={(e) => set('_remaining', e.target.value)} className={inputCls} data-testid="sponsor-edit-remaining" />
            ) : (
              <p className={`text-xs py-2 ${mutedCls}`}>{settings.term_months} months, starting when the logo is added</p>
            )}
          </div>
          <div className="md:col-span-2">
            <label className={lbl}>Sponsor website (optional)</label>
            <Input value={draft.website_url || ''} onChange={(e) => set('website_url', e.target.value)}
              placeholder="https://sponsor.com" className={inputCls} data-testid="sponsor-edit-website" />
          </div>
          <div className="flex items-center gap-2">
            <input type="checkbox" id="sp-active" checked={!!draft.active}
              onChange={(e) => set('active', e.target.checked)} data-testid="sponsor-edit-active" />
            <label htmlFor="sp-active" className={`text-xs ${textCls}`}>Active (show on the website while the term runs)</label>
          </div>
        </div>
      </div>
      <div className="flex justify-end gap-2">
        <Button size="sm" variant="ghost" onClick={onCancel} data-testid="sponsor-edit-cancel"><X className="h-4 w-4 mr-1" />Cancel</Button>
        <Button size="sm" onClick={onSave} disabled={!draft.name || saving}
          className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="sponsor-edit-save">
          {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Save className="h-4 w-4 mr-1" />}
          {draft.id ? 'Save changes' : 'Add sponsor'}
        </Button>
      </div>
    </div>
  );
}

function TierManager({ orgId, tiers, sports, reload, isDark, cardCls, textCls, mutedCls, inputCls, usageCount }) {
  const [newName, setNewName] = useState('');
  const [newSport, setNewSport] = useState('');
  const [busy, setBusy] = useState(false);
  const selSm = `rounded-md px-2 py-1 text-sm border ${isDark ? 'bg-slate-700/50 border-slate-600 text-white' : 'bg-white border-slate-300'}`;

  const add = async () => {
    if (!newName.trim()) return;
    setBusy(true);
    try { await axios.post(`${API}/api/sponsors/${orgId}/tiers`, { name: newName.trim(), sport: newSport }); setNewName(''); await reload(); }
    catch (err) { alert(errMsg(err, 'Create failed')); }
    finally { setBusy(false); }
  };
  const patch = async (id, body) => {
    try { await axios.patch(`${API}/api/sponsors/${orgId}/tiers/${id}`, body); await reload(); }
    catch (err) { alert(errMsg(err, 'Update failed')); }
  };
  const del = async (id) => {
    if (!window.confirm('Delete this tier?')) return;
    try { await axios.delete(`${API}/api/sponsors/${orgId}/tiers/${id}`); await reload(); }
    catch (err) { alert(errMsg(err, 'Delete failed')); }
  };
  const sorted = [...tiers].sort((a, b) => (a.sport || '').localeCompare(b.sport || '') || (a.order || 0) - (b.order || 0));

  return (
    <Card className={cardCls} data-testid="tier-manager">
      <CardContent className="p-3 space-y-3">
        <div>
          <span className={`text-sm font-semibold ${textCls}`}>Sponsor Tiers</span>
          <p className={`text-xs mt-0.5 ${mutedCls}`}>
            Tiers belong to a sport (or to all sports). A sponsor's Tier list shows its sport's tiers. A higher
            <strong> carousel weight</strong> means that tier's sponsors appear more prominently/often.
          </p>
        </div>
        <div className="flex items-end gap-2 flex-wrap">
          <div className="flex-1 min-w-[160px]">
            <label className={`text-[11px] font-semibold uppercase tracking-wide ${mutedCls}`}>New tier name</label>
            <Input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="e.g., Champion" className={inputCls} data-testid="tier-new-name" />
          </div>
          <div>
            <label className={`text-[11px] font-semibold uppercase tracking-wide block ${mutedCls}`}>Sport</label>
            <select value={newSport} onChange={(e) => setNewSport(e.target.value)} className={selSm} data-testid="tier-new-sport">
              <option value="">All sports</option>
              {sports.map((n) => <option key={n} value={n.toLowerCase()}>{n}</option>)}
            </select>
          </div>
          <Button size="sm" onClick={add} disabled={busy || !newName.trim()} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="tier-add-btn">
            {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Plus className="h-4 w-4 mr-1" />}Add tier
          </Button>
        </div>
        <div className="space-y-2">
          {sorted.map((t) => (
            <div key={t.id} className={`flex items-center gap-3 p-2 rounded border flex-wrap ${isDark ? 'bg-slate-900/40 border-slate-700' : 'bg-slate-50 border-slate-200'}`} data-testid={`tier-row-${t.slug}`}>
              <Award className="h-4 w-4 text-amber-500 shrink-0" />
              <div className="flex-1 min-w-[120px]">
                <div className={`text-sm font-medium ${textCls}`}>{t.name}</div>
                <div className={`text-[11px] ${mutedCls}`}>{usageCount(t.slug)} sponsor(s)</div>
              </div>
              <select value={t.sport || ''} onChange={(e) => patch(t.id, { sport: e.target.value })} className={selSm} data-testid={`tier-sport-${t.slug}`}>
                <option value="">All sports</option>
                {sports.map((n) => <option key={n} value={n.toLowerCase()}>{n}</option>)}
              </select>
              <input defaultValue={t.price || ''} key={`${t.id}-${t.price}`} placeholder="Price"
                onBlur={(e) => { if (e.target.value.trim() !== (t.price || '')) patch(t.id, { price: e.target.value.trim() }); }}
                className={`${selSm} w-20`} data-testid={`tier-price-${t.slug}`} />
              <div className="flex items-center gap-1">
                <label className={`text-[10px] uppercase ${mutedCls}`}>Carousel weight</label>
                <select value={t.carousel_weight ?? 1} onChange={(e) => patch(t.id, { carousel_weight: parseInt(e.target.value, 10) })}
                  className={selSm} data-testid={`tier-weight-${t.slug}`}>
                  {[1, 2, 3, 4, 5].map((n) => <option key={n} value={n}>{n}</option>)}
                </select>
              </div>
              <Button size="sm" variant="ghost" className="h-7 px-2 text-xs text-red-400 hover:text-red-300" onClick={() => del(t.id)} data-testid={`tier-delete-${t.slug}`}>
                <Trash2 className="h-3 w-3" />
              </Button>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
